import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PinPad } from "@/components/common/PinPad";
import { MyProfile } from "@/components/common/MyProfile";
import { STATIONS, allowedStations } from "@/lib/stations";
import { LayoutGrid, BarChart3, CreditCard, ChefHat, UserRound, LogOut, ChevronDown, Lock } from "lucide-react";

const ICONS = { manager: BarChart3, pos: CreditCard, kds: ChefHat };
const DEFAULT_LOCK = { pos: 5, manager: 5, kds: 10 };
const LOCK_KEY = "novanext_locked";

// Bandeau réactif au scroll : reste sticky (toujours accessible) et devient compact
// quand on descend, complet quand on remonte. Fonctionne souris / trackpad / tactile / PWA.
export function useCompactHeader(threshold = 48) {
  const [compact, setCompact] = useState(false);
  const last = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y < threshold) setCompact(false);
      else if (y > last.current + 4) setCompact(true);
      else if (y < last.current - 4) setCompact(false);
      last.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return compact;
}

// Verrouillage automatique après inactivité (minutes configurables par restaurant).
function useAutoLock(minutes, locked, onLock) {
  useEffect(() => {
    if (!minutes || locked) return;
    let t;
    const arm = () => { clearTimeout(t); t = setTimeout(onLock, minutes * 60 * 1000); };
    const evs = ["pointerdown", "keydown", "touchstart", "wheel"];
    evs.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, arm)); };
  }, [minutes, locked, onLock]);
}

// Bouton utilisateur du header : « Prénom · SRV-002 », Mon profil, Changer de poste, Verrouiller, Se déconnecter / Changer d'employé.
export function StationSwitcher({ current, slug }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [locked, setLocked] = useState(() => localStorage.getItem(LOCK_KEY) === "1");
  const [uPin, setUPin] = useState("");
  const [uErr, setUErr] = useState("");
  const [uLoading, setULoading] = useState(false);
  const [lockMin, setLockMin] = useState(DEFAULT_LOCK[current] || 5);
  const effSlug = slug || user?.restaurant_slug;

  useEffect(() => {
    if (!effSlug) return;
    api.getRestaurant(effSlug).then((r) => {
      const v = current === "kds" ? r?.lock_minutes_kds : r?.lock_minutes_pos;
      setLockMin(Number(v) || DEFAULT_LOCK[current] || 5);
    }).catch(() => {});
  }, [effSlug, current]);

  const lockStation = () => { setOpen(false); localStorage.setItem(LOCK_KEY, "1"); setLocked(true); setUPin(""); setUErr(""); };
  useAutoLock(lockMin, locked, lockStation);

  const doLogout = async () => {
    setOpen(false);
    localStorage.removeItem(LOCK_KEY);
    await logout();
    navigate(`/login?station=${current}&slug=${effSlug || ""}`);
  };

  const unlockPin = async () => {
    if (uPin.length < 4) { setUErr("PIN à 4 chiffres minimum"); return; }
    setULoading(true); setUErr("");
    try { await api.unlock(uPin); localStorage.removeItem(LOCK_KEY); setLocked(false); setUPin(""); }
    catch (e) {
      const st = e?.response?.status;
      if (st === 429 || st === 401 && /reconnect/i.test(String(e?.response?.data?.detail || ""))) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Reconnexion requise");
        await doLogout();
        return;
      }
      setUErr(formatApiError(e?.response?.data?.detail) || "PIN incorrect"); setUPin("");
    } finally { setULoading(false); }
  };

  const stations = allowedStations(user).filter((s) => STATIONS[s]);
  const others = stations.filter((s) => s !== current);
  const goStation = (key) => { setOpen(false); if (key !== current) navigate(STATIONS[key].route); };
  const firstName = (user?.name || user?.email || "").split(" ")[0];
  const code = user?.employee_code;
  const kds = current === "kds";

  return (
    <>
      <div className="relative">
        <button data-testid="station-switcher-btn" onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#111F1A] border border-[#1B3028] text-stone-200 hover:border-[#C69C4A]/50 text-xs font-semibold px-3 py-2 transition-colors max-w-[52vw] sm:max-w-none">
          <UserRound className="w-4 h-4 text-[#C69C4A] shrink-0" />
          <span data-testid="header-user-identity" className="truncate">{firstName}{code && <span className="font-mono text-stone-400"> · {code}</span>}</span>
          <ChevronDown className="w-3.5 h-3.5 shrink-0" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div data-testid="station-switcher-menu" className="absolute right-0 top-11 z-50 w-64 rounded-2xl bg-[#111F1A] border border-[#1B3028] py-2 shadow-2xl">
              <div className="px-3 pb-2 pt-1 border-b border-[#1B3028]">
                <p data-testid="menu-user-name" className="text-sm text-stone-100 font-semibold truncate">{user?.name || user?.email}</p>
                <p className="text-[11px] text-stone-400 truncate">{code && <span className="font-mono text-[#C69C4A]">{code} · </span>}{STATIONS[current]?.label}</p>
              </div>
              <button data-testid="menu-my-profile" onClick={() => { setOpen(false); setProfileOpen(true); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-stone-200 hover:bg-[#1A3D32] transition-colors">
                <UserRound className="w-4 h-4 text-[#C69C4A]" /> Mon profil
              </button>
              {others.length > 0 && <>
                <p className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-widest text-stone-500">Changer de poste</p>
                {others.map((s) => {
                  const Icon = ICONS[s] || LayoutGrid;
                  return (
                    <button key={s} data-testid={`switch-station-${s}`} onClick={() => goStation(s)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-stone-200 hover:bg-[#1A3D32] transition-colors">
                      <Icon className="w-4 h-4 text-[#C69C4A]" /> {STATIONS[s].label}
                    </button>
                  );
                })}
              </>}
              <div className="my-1.5 border-t border-[#1B3028]" />
              <button data-testid="lock-station-btn" onClick={lockStation}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-stone-200 hover:bg-[#1A3D32] transition-colors">
                <Lock className="w-4 h-4 text-[#C69C4A]" /> Verrouiller
              </button>
              <button data-testid="switch-logout-btn" onClick={doLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-300 hover:bg-red-500/10 transition-colors">
                <LogOut className="w-4 h-4" /> Se déconnecter / Changer d'employé
              </button>
            </div>
          </>
        )}
      </div>

      <MyProfile open={profileOpen} onClose={() => setProfileOpen(false)} />

      {locked && (
        <div data-testid="lock-overlay" className={`fixed inset-0 z-[100] px-5 ${kds ? "bg-[#08110E]/70 flex items-start justify-end pt-20" : "bg-[#08110E]/98 backdrop-blur-sm grid place-items-center"}`}>
          <div className={`w-full max-w-sm text-center ${kds ? "rounded-3xl bg-[#0C1713] border border-[#1B3028] p-5 shadow-2xl" : ""}`}>
            <div className="w-12 h-12 rounded-2xl bg-[#C69C4A] text-[#0C1713] grid place-items-center mx-auto mb-3"><Lock className="w-6 h-6" /></div>
            <h2 data-testid="lock-greeting" className="font-serif-display text-2xl text-stone-100">Bonjour {firstName}{code && <span className="font-mono text-lg text-[#C69C4A]"> — {code}</span>}</h2>
            <p className="text-xs text-stone-400 mt-1 mb-5">{kds ? "Les tickets restent affichés et à jour. " : ""}Saisissez votre PIN personnel pour reprendre · le service en cours est conservé.</p>
            <PinPad value={uPin} onChange={setUPin} max={6} disabled={uLoading} dark />
            {uErr && <p data-testid="lock-error" className="text-center text-sm text-red-400 mt-3">{uErr}</p>}
            <button data-testid="lock-unlock-pin" onClick={unlockPin} disabled={uLoading || uPin.length < 4}
              className="mt-4 w-full h-12 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold disabled:opacity-40">
              {uLoading ? "Vérification…" : "Déverrouiller"}
            </button>
            <button data-testid="lock-switch-user" onClick={doLogout} className="mt-3 text-xs text-stone-400 hover:text-stone-200 inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> Ce n'est pas moi · changer d'employé</button>
          </div>
        </div>
      )}
    </>
  );
}
